import type { Type } from '@angular/core';
import { createNgModuleRef, inject, Injectable, Injector } from '@angular/core';
import type { Params } from '@angular/router';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  from,
  Observable,
  of as observableOf,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  shareReplay,
  skip,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import type { CandidateVisitedSnapshot } from '@core/dashboard-types';
import { I18nService } from '@core/i18n-legacy';
import { MailboxCache } from '@core/mailbox-data';
import type { ConversationStartersContext } from '@core/tellent-assistant-types';
import {
  DialogService,
  ProtectedControlsDialogComponent,
  RtPlatform,
} from '@core/user-interface-legacy';
import { macroTask } from '@core/utils-legacy';
import { PageTitleService } from '@recruitee/common-logic';
import { VisitedEntitiesHistoryService } from '@recruitee/dashboard-logic';

import { candidateResolver } from './resolvers/candidate.resolver';
import { selectCandidateData } from '../features/candidate/store/feature';

type CandidateDialogData =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogData;
type CandidateDialogComponent =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogComponent;

@Injectable({ providedIn: 'root' })
export class CandidateDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly platform = inject(RtPlatform);
  private readonly mailboxCache = inject(MailboxCache);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly pageTitleService = inject(PageTitleService);
  private readonly i18nService = inject(I18nService);
  private readonly injector = inject(Injector);
  private readonly visitedEntitiesHistoryService = inject<
    VisitedEntitiesHistoryService<CandidateVisitedSnapshot>
  >(VisitedEntitiesHistoryService<CandidateVisitedSnapshot>);

  private readonly ngOnDestroy$ = new Subject<void>();
  private readonly isOpen$ = new Subject<boolean>();
  private readonly candidateData$ = new BehaviorSubject<CandidateDialogData>(<any>null);

  public readonly conversationStartersContext$: Observable<ConversationStartersContext | null> =
    combineLatest([
      this.isOpen$.pipe(startWith(false)),
      this.store.select(selectCandidateData),
    ]).pipe(
      map(([isOpen, candidate]) =>
        isOpen && candidate
          ? ((<const>{
              type: 'recruitment_candidate',
              params: { candidateId: candidate.id, candidateName: candidate.name },
            }) satisfies ConversationStartersContext)
          : null,
      ),
    );

  private candidateDialogComponent$ = from(import('../features/candidate/candidate.module')).pipe(
    map(({ CandidateDialogComponent, RtCandidateFeatureModule }) => ({
      CandidateDialogComponent,
      candidateFeatureModuleRef: createNgModuleRef(RtCandidateFeatureModule, this.injector),
    })),
    shareReplay(),
  );

  constructor() {
    this.candidateDialogComponent$.pipe(take(1)).subscribe();
  }

  public init(): void {
    this.isOpen$
      .pipe(
        distinctUntilChanged(),
        switchMap(isOpen =>
          this.candidateDialogComponent$.pipe(
            switchMap(({ CandidateDialogComponent, candidateFeatureModuleRef }) =>
              isOpen
                ? this.openDialog(
                    CandidateDialogComponent,
                    candidateFeatureModuleRef.injector,
                    this.candidateData$,
                  )
                : EMPTY,
            ),
          ),
        ),
      )
      .subscribe();

    combineLatest([
      this.store.select(selectCandidateData),
      this.isOpen$,
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([candidate, isOpen]) => {
        if (!candidate || !isOpen) return;

        const profileString = this.i18nService.translate('app.candidate.profile');
        const pageTitle = `${candidate.name} - ${profileString}`;
        this.pageTitleService.setCustomTitle(pageTitle);
      });

    combineLatest([
      this.route.queryParams.pipe(filterBoolean),
      this.store.select(AccountSelectors.currentCompany).pipe(filterBoolean, take(1)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([queryParams]) =>
        queryParams.candidate ? this.open(queryParams) : this.close(),
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public open(queryParams: Params): void {
    this.candidateData$.next({
      candidateId: +queryParams.candidate,
      tab: queryParams.tab ? queryParams.tab : undefined,
      tabKey: queryParams.tabKey ? queryParams.tabKey : undefined,
      offerId: queryParams.offerId ? queryParams.offerId : undefined,
      openInterview: queryParams.openInterview ? true : undefined,
      interviewOfferId: queryParams.interviewOfferId ? +queryParams.interviewOfferId : undefined,
      interviewTalentPoolId: queryParams.interviewTalentPoolId
        ? +queryParams.interviewTalentPoolId
        : undefined,
      interviewTemplateId: queryParams.interviewTemplateId
        ? +queryParams.interviewTemplateId
        : undefined,
      interviewEdit: queryParams.interviewEdit ? !!queryParams.interviewEdit : undefined,
      interviewResultId: queryParams.interviewResultId ? +queryParams.interviewResultId : undefined,
      interviewResultRequestId: queryParams.interviewResultRequestId
        ? +queryParams.interviewResultRequestId
        : undefined,
      fromHiddenEvaluations: queryParams.fromHiddenEvaluations
        ? !!queryParams.fromHiddenEvaluations
        : undefined,
      matchId: queryParams.matchId ? +queryParams.matchId : undefined,
    });
    this.isOpen$.next(true);

    candidateResolver(this.store, selectCandidateData)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(candidate => this.visitedEntitiesHistoryService.updateHistory(candidate));
  }

  public close(): void {
    this.isOpen$.next(false);
  }

  private openDialog(
    component: Type<CandidateDialogComponent>,
    injector: Injector,
    data$: BehaviorSubject<CandidateDialogData>,
  ) {
    return new Observable(observer => {
      const destroy$ = new Subject<void>();
      const data = data$.value;
      const dialogRef = this.dialogService.openCustom(
        component,
        {
          showCloseButton: false,
          disableClose: false,
          containerOptions: {
            hasPadding: false,
          },
          injector,
          closeGuard: () => {
            const { protectedControls } = dialogRef.componentInstance;
            if (protectedControls.locked) {
              return this.dialogService
                .openCustom(ProtectedControlsDialogComponent, {}, {})
                .afterClosed.pipe(
                  tap(result => {
                    if (result) {
                      this.mailboxCache.remove(
                        this.mailboxCache.key('candidate', data.candidateId),
                      );
                    }
                  }),
                  share(),
                );
            } else {
              return observableOf(true);
            }
          },
        },
        this.getInitialData(data$.value),
      );

      data$.pipe(skip(1), takeUntil(destroy$)).subscribe(data => {
        dialogRef.componentInstance.setDialogData(data, true);
      });
      dialogRef.afterClosed.pipe(take(1)).subscribe(() => {
        macroTask(() => this.clearUrlParams());
        this.isOpen$.next(false);
        observer.complete();
      });
      return () => {
        destroy$.next();
        destroy$.complete();
        dialogRef.close();
      };
    });
  }

  private getInitialData<T extends CandidateDialogData>(data: T): CandidateDialogData {
    return {
      candidateId: data.candidateId,
      offerId: data.offerId,
      tab: data.tab,
      tabKey: data.tabKey,
      openInterview: data.openInterview,
      interviewOfferId: data.interviewOfferId,
      interviewTalentPoolId: data.interviewTalentPoolId,
      interviewTemplateId: data.interviewTemplateId,
      interviewEdit: data.interviewEdit,
      interviewResultId: data.interviewResultId,
      interviewResultRequestId: data.interviewResultRequestId,
      fromHiddenEvaluations: data.fromHiddenEvaluations,
      matchId: data.matchId ? +data.matchId : undefined,
    };
  }

  private clearUrlParams() {
    this.router.navigate([], {
      queryParams: {
        candidate: null,
        offerId: null,
        tab: null,
        tabKey: null,
        subtab: null,
        messagesMode: null,
        smsThread: null,
        openInterview: null,
        interviewOfferId: null,
        interviewTalentPoolId: null,
        interviewTemplateId: null,
        interviewEdit: null,
        interviewResultId: null,
        interviewResultRequestId: null,
        fromHiddenEvaluations: null,
        matchId: null,
      } satisfies Omit<Record<keyof CandidateDialogData, null>, 'candidateId'> & {
        candidate: null;
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }
}
